import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { fits, fittedSize, type Frame, frameFor, header, note, statusDot, textWidth, TYPE, wrapText } from './kit.js';

export interface ComparisonOption {
  id: string;
  label: string;
  /** One factual clause identifying the option. */
  detail?: string;
}

export interface ComparisonCriterion {
  id: string;
  label: string;
  unit: string;
  /** Measured value for the first option. Not a position: options are ordered, not placed. */
  first: number;
  /** Measured value for the second option. */
  second: number;
  /** Which direction of the measurement is preferable for this decision. */
  better: 'lower' | 'higher';
  /** Exactly one criterion may decide the outcome regardless of the tally. */
  decisive?: boolean;
  /** How many decimals the values carry. Never invents precision. */
  decimals?: number;
}

export interface ComparisonSpec extends PrimitiveSpec {
  family: 'comparison';
  view: 'table';
  subject: string;
  /** The either/or being settled, in one line. */
  question: string;
  options: [ComparisonOption, ComparisonOption];
  criteria: ComparisonCriterion[];
  /** The option the decisive criterion selects. Checked against the data. */
  verdictOptionId: string;
  /** Why the decisive criterion outranks the tally. */
  verdictReason: string;
  title: string;
  subtitle: string;
}

export const comparisonCriterionId = (id: string): string => `comparison.criterion.${id}`;
export const comparisonOptionId = (id: string): string => `comparison.option.${id}`;
export const comparisonCellId = (criterionId: string, optionId: string): string => `comparison.cell.${criterionId}.${optionId}`;
export const COMPARISON_VERDICT_ID = 'comparison.verdict';

interface Metrics {
  frame: Frame;
  showRatio: boolean;
  criterionW: number;
  ratioW: number;
  valueW: number;
  headRow: number;
  labelSize: number;
  labelLines: string[][];
  rowH: number;
  tableTop: number;
  bodyTop: number;
  tableBottom: number;
  verdictTop: number;
  reasonLines: string[];
  verdictH: number;
  noteY: number;
  required: number;
}

/** One layout, computed once, shared by the validator and the compiler. */
function metrics(spec: ComparisonSpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const showRatio = frame.tier === 'wide';
  const criterionW = frame.tier === 'wide' ? 186 : frame.tier === 'phone' ? 150 : 132;
  const ratioW = showRatio ? 128 : 0;
  const valueW = (frame.contentWidth - criterionW - ratioW) / 2;
  const headRow = showRatio ? 54 : 42;
  const labelSize = frame.tier === 'compact' ? TYPE.micro : TYPE.label;
  const labelLines = spec.criteria.map((criterion) => wrapText(criterion.label, labelSize, criterionW - 20, true));
  const rowH = 24 + 16 * Math.max(...labelLines.map((lines) => lines.length));
  const tableTop = frame.top;
  const bodyTop = tableTop + headRow;
  const tableBottom = bodyTop + spec.criteria.length * rowH;
  const verdictTop = tableBottom + 14;
  const reasonLines = wrapText(spec.verdictReason, TYPE.micro, frame.contentWidth - 22, true);
  const verdictH = 40 + reasonLines.length * 14 + 12;
  const noteY = verdictTop + verdictH + 20;
  return {
    frame, showRatio, criterionW, ratioW, valueW, headRow, labelSize, labelLines, rowH,
    tableTop, bodyTop, tableBottom, verdictTop, reasonLines, verdictH, noteY,
    required: noteY + 14,
  };
}

/**
 * A comparison is not a two-column table.
 *
 * What makes it a comparison is that every criterion carries a *direction of
 * preference*, so the winner of each row is derived rather than asserted; that
 * the two options are exhaustive for the question asked; and that one criterion
 * can decide the outcome against the tally. This compiler computes every verdict
 * from the measurements and refuses a spec whose stated conclusion does not
 * follow from its own decisive row.
 */
export function compileComparison(spec: ComparisonSpec): ResolvedFigure {
  validateComparison(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const [firstOption, secondOption] = spec.options;
  const warnings: string[] = [];
  const tableX = frame.margin;

  const winners = spec.criteria.map((criterion) => winnerOf(criterion, spec));
  const tally = spec.options.map((option) => winners.filter((winner) => winner === option.id).length);
  const decisive = spec.criteria.find((criterion) => criterion.decisive)!;

  header(builder, frame, 'comparison', `EITHER / OR · ${spec.subject.toUpperCase()}`, [
    spec.question.toUpperCase(),
    `${spec.criteria.length} CRITERIA · ONE DECIDES`,
  ]);

  builder.rect('comparison.table', 'comparison', 'structure', box(tableX, m.tableTop, frame.contentWidth, m.tableBottom - m.tableTop), { radius: 4 });
  builder.rect('comparison.head', 'comparison', 'header', box(tableX, m.tableTop, frame.contentWidth, m.headRow));
  builder.text('comparison.head.criterion', 'comparison', 'annotation', point(tableX + 10, m.tableTop + 26), 'CRITERION', {
    size: TYPE.micro,
    mono: true,
    weight: 700,
    uppercase: true,
  });

  const columns = [
    { option: firstOption, x: tableX + m.criterionW, index: 0 },
    { option: secondOption, x: tableX + m.criterionW + m.valueW, index: 1 },
  ];
  // Either both option details fit at full size or neither is drawn: an
  // asymmetric head would read as a difference between the options.
  const showDetails = m.showRatio && spec.options.every((option) => option.detail !== undefined && fits(option.detail, TYPE.micro, m.valueW - 10, true));
  for (const column of columns) {
    const semanticId = comparisonOptionId(column.option.id);
    const centre = column.x + m.valueW / 2;
    builder.text(`${semanticId}.label`, semanticId, 'structure', point(centre, m.tableTop + 21), column.option.label, {
      size: fittedSize(column.option.label, TYPE.label, m.valueW - 12, true),
      mono: true,
      weight: 700,
      align: 'middle',
    });
    const longWins = `WINS ${tally[column.index]} OF ${spec.criteria.length}`;
    const wins = fits(longWins, TYPE.micro, m.valueW - 10, true, true) ? longWins : `${tally[column.index]} OF ${spec.criteria.length}`;
    builder.text(`${semanticId}.tally`, semanticId, 'annotation', point(centre, m.tableTop + 35), wins, {
      size: TYPE.micro,
      mono: true,
      weight: 700,
      align: 'middle',
      uppercase: true,
    });
    if (showDetails) {
      builder.text(`${semanticId}.detail`, semanticId, 'annotation', point(centre, m.tableTop + 48), column.option.detail!, {
        size: TYPE.micro,
        mono: true,
        weight: 500,
        align: 'middle',
      });
    }
    builder.line(`${semanticId}.rule`, semanticId, 'construction', point(column.x, m.tableTop), point(column.x, m.tableBottom));
    builder.anchor(`${semanticId}.head`, semanticId, point(centre, m.tableTop + m.headRow), 'down');
    builder.hit(semanticId, box(column.x, m.tableTop, m.valueW, m.headRow));
    if (column.option.detail) {
      builder.relation(`${semanticId}.identity`, 'identifies', semanticId, 'comparison', `${column.option.label}: ${column.option.detail}`);
    }
  }

  const ratioX = tableX + m.criterionW + m.valueW * 2;
  if (m.showRatio) {
    builder.line('comparison.ratio.rule', 'comparison', 'construction', point(ratioX, m.tableTop), point(ratioX, m.tableBottom));
    builder.text('comparison.head.ratio', 'comparison', 'annotation', point(ratioX + 10, m.tableTop + 26), 'RATIO · LARGER', {
      size: TYPE.micro,
      mono: true,
      weight: 700,
      uppercase: true,
    });
  }

  spec.criteria.forEach((criterion, index) => {
    const semanticId = comparisonCriterionId(criterion.id);
    const y = m.bodyTop + index * m.rowH;
    const winner = winners[index]!;
    const isDecisive = criterion.decisive === true;
    builder.rect(`${semanticId}.row`, semanticId, isDecisive ? 'active' : 'surface', box(tableX, y, frame.contentWidth, m.rowH), {
      label: `${criterion.label}: ${criterion.better} is better`,
      interactive: true,
    });
    if (index > 0) builder.line(`${semanticId}.rule`, semanticId, 'construction', point(tableX, y), point(tableX + frame.contentWidth, y));

    builder.text(`${semanticId}.direction`, semanticId, 'annotation', point(tableX + 10, y + 16), `${criterion.better.toUpperCase()} WINS`, {
      size: TYPE.micro,
      mono: true,
      weight: 700,
      uppercase: true,
    });
    m.labelLines[index]!.forEach((line, lineIndex) => {
      builder.text(`${semanticId}.label.${lineIndex}`, semanticId, 'structure', point(tableX + 10, y + 33 + lineIndex * 16), line, {
        size: m.labelSize,
        mono: true,
        weight: 700,
      });
    });

    const valueBaseline = y + m.rowH / 2 - 1;
    columns.forEach((column) => {
      const value = column.index === 0 ? criterion.first : criterion.second;
      const cellSemantic = comparisonCellId(criterion.id, column.option.id);
      const centre = column.x + m.valueW / 2;
      const text = format(value, criterion.decimals ?? 0);
      const valueSize = fittedSize(text, TYPE.value + 2, m.valueW - 26, true);
      builder.text(`${cellSemantic}.value`, cellSemantic, 'structure', point(centre, valueBaseline), text, {
        size: valueSize,
        mono: true,
        weight: 700,
        align: 'middle',
      });
      builder.text(`${cellSemantic}.unit`, cellSemantic, 'annotation', point(centre, valueBaseline + 15), criterion.unit, {
        size: fittedSize(criterion.unit, TYPE.micro, m.valueW - 12, true),
        mono: true,
        weight: 500,
        align: 'middle',
      });
      if (winner === column.option.id) {
        statusDot(builder, `${cellSemantic}.dot`, cellSemantic, point(centre - textWidth(text, valueSize, true) / 2 - 10, valueBaseline - 5), 'positive');
      }
      builder.anchor(`${cellSemantic}.centre`, cellSemantic, point(centre, y + m.rowH / 2));
      builder.hit(cellSemantic, box(column.x, y, m.valueW, m.rowH));
      builder.relation(
        `${cellSemantic}.measure`,
        winner === column.option.id ? 'wins-criterion' : 'loses-criterion',
        cellSemantic,
        semanticId,
        `${column.option.label} ${criterion.label.toLowerCase()}: ${text} ${criterion.unit}`,
      );
    });

    if (m.showRatio) {
      const ratio = ratioOf(criterion, spec);
      builder.text(`${semanticId}.ratio`, semanticId, 'structure', point(ratioX + 10, valueBaseline), ratio.value, {
        size: TYPE.label,
        mono: true,
        weight: 700,
      });
      builder.text(`${semanticId}.ratio.detail`, semanticId, 'annotation', point(ratioX + 10, valueBaseline + 15), ratio.detail, {
        size: TYPE.micro,
        mono: true,
        weight: 500,
      });
    }

    builder.anchor(`${semanticId}.left`, semanticId, point(tableX, y + m.rowH / 2), 'left');
    if (isDecisive) {
      builder.anchor(`${semanticId}.decides`, semanticId, point(tableX, y + m.rowH), 'down');
      builder.relation(`${semanticId}.decides`, 'decides', semanticId, COMPARISON_VERDICT_ID, `${criterion.label} settles the choice`);
    }
  });

  // The verdict is derived: the decisive row's winner, stated with its reason.
  const winnerOption = spec.options.find((option) => option.id === spec.verdictOptionId)!;
  const verdictBounds = box(tableX, m.verdictTop, frame.contentWidth, m.verdictH);
  builder.rect(`${COMPARISON_VERDICT_ID}.plate`, COMPARISON_VERDICT_ID, 'header', verdictBounds, { radius: 4, label: 'verdict', interactive: true });
  const longKicker = `DECIDED BY ${decisive.label.toUpperCase()}`;
  const kicker = fits(longKicker, TYPE.micro, frame.contentWidth - 24, true, true) ? longKicker : 'DECIDED BY THE MARKED ROW';
  builder.text(`${COMPARISON_VERDICT_ID}.kicker`, COMPARISON_VERDICT_ID, 'annotation', point(tableX + 11, m.verdictTop + 17), kicker, {
    size: TYPE.micro,
    mono: true,
    weight: 700,
    uppercase: true,
  });
  statusDot(builder, `${COMPARISON_VERDICT_ID}.dot`, COMPARISON_VERDICT_ID, point(tableX + 15, m.verdictTop + 31), 'positive');
  builder.text(`${COMPARISON_VERDICT_ID}.choice`, COMPARISON_VERDICT_ID, 'structure', point(tableX + 26, m.verdictTop + 36), winnerOption.label.toUpperCase(), {
    size: TYPE.heading,
    mono: true,
    weight: 700,
  });
  m.reasonLines.forEach((line, lineIndex) => {
    builder.text(`${COMPARISON_VERDICT_ID}.reason.${lineIndex}`, COMPARISON_VERDICT_ID, 'annotation', point(tableX + 11, m.verdictTop + 54 + lineIndex * 14), line, {
      size: TYPE.micro,
      mono: true,
      weight: 500,
    });
  });
  builder.anchor(`${COMPARISON_VERDICT_ID}.top`, COMPARISON_VERDICT_ID, point(tableX + frame.contentWidth / 2, m.verdictTop), 'up');
  builder.hit(COMPARISON_VERDICT_ID, verdictBounds);
  builder.relation(`${COMPARISON_VERDICT_ID}.follows`, 'follows-from', COMPARISON_VERDICT_ID, comparisonCriterionId(decisive.id), `${winnerOption.label} wins the criterion that decides`);

  note(
    builder,
    'comparison.tally-note',
    'comparison',
    point(tableX, m.noteY),
    tallyNote(tally[0]!, tally[1]!, frame.contentWidth),
  );
  if (!m.showRatio) warnings.push('Ratios are held back at this width; every measured value is unchanged.');

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: `decisive:${decisive.id}`,
    active: [comparisonCriterionId(decisive.id), ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateComparison(spec: ComparisonSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'comparison' || spec.view !== 'table') throw new Error('comparison: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('comparison: provenance is required');
  if (!spec.subject.trim() || !spec.question.trim() || !spec.title.trim() || !spec.subtitle.trim()) throw new Error('comparison: subject, question, title and subtitle are required');
  if (spec.options.length !== 2) throw new Error('comparison: require exactly two options');
  if (spec.options[0].id === spec.options[1].id) throw new Error('comparison: option IDs must be unique');
  for (const option of spec.options) {
    if (!/^[a-z][a-z0-9-]*$/.test(option.id)) throw new Error(`comparison: invalid option id ${option.id}`);
    if (!option.label.trim() || option.label.length > 16) throw new Error(`comparison: option ${option.id} needs a label of 1…16 characters`);
  }
  if (spec.criteria.length < 2 || spec.criteria.length > 6) throw new Error('comparison: require 2…6 criteria');
  const ids = new Set<string>();
  let decisives = 0;
  for (const criterion of spec.criteria) {
    if (!/^[a-z][a-z0-9-]*$/.test(criterion.id)) throw new Error(`comparison: invalid criterion id ${criterion.id}`);
    if (ids.has(criterion.id)) throw new Error(`comparison: duplicate criterion id ${criterion.id}`);
    ids.add(criterion.id);
    if (!criterion.label.trim() || criterion.label.length > 30) throw new Error(`comparison: criterion ${criterion.id} needs a label of 1…30 characters`);
    if (!criterion.unit.trim() || criterion.unit.length > 12) throw new Error(`comparison: criterion ${criterion.id} needs a unit of 1…12 characters`);
    if (!Number.isFinite(criterion.first) || !Number.isFinite(criterion.second)) throw new Error(`comparison: criterion ${criterion.id} needs two measured values`);
    if (criterion.first === criterion.second) throw new Error(`comparison: criterion ${criterion.id} does not separate the options`);
    if (criterion.better !== 'lower' && criterion.better !== 'higher') throw new Error(`comparison: criterion ${criterion.id} needs a direction of preference`);
    if (criterion.decisive) decisives += 1;
  }
  if (decisives !== 1) throw new Error('comparison: exactly one criterion must be marked decisive');
  if (!spec.options.some((option) => option.id === spec.verdictOptionId)) throw new Error('comparison: the verdict must name one of the options');
  const decisive = spec.criteria.find((criterion) => criterion.decisive)!;
  if (winnerOf(decisive, spec) !== spec.verdictOptionId) throw new Error('comparison: the verdict contradicts its own decisive criterion');
  if (!spec.verdictReason.trim()) throw new Error('comparison: the verdict requires a reason');
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`comparison: this table needs at least ${Math.ceil(required)}px of height`);
}

function tallyNote(left: number, right: number, available: number): string {
  const candidates = left === right
    ? [`TALLY IS ${left}–${right} · COUNTING CRITERIA DECIDES NOTHING`, `TALLY ${left}–${right} · COUNTING DECIDES NOTHING`, `TALLY ${left}–${right} · DECIDES NOTHING`]
    : [`TALLY ${left}–${right} · THE TALLY IS NOT THE DECISION`, `TALLY ${left}–${right} · NOT THE DECISION`];
  return candidates.find((candidate) => fits(candidate, TYPE.micro, available, true)) ?? candidates[candidates.length - 1]!;
}

function winnerOf(criterion: ComparisonCriterion, spec: ComparisonSpec): string {
  const firstWins = criterion.better === 'lower' ? criterion.first < criterion.second : criterion.first > criterion.second;
  return firstWins ? spec.options[0].id : spec.options[1].id;
}

function ratioOf(criterion: ComparisonCriterion, spec: ComparisonSpec): { value: string; detail: string } {
  const high = Math.max(criterion.first, criterion.second);
  const low = Math.min(criterion.first, criterion.second);
  const ratio = high / low;
  return {
    value: `${format(ratio, ratio >= 10 ? 0 : 2)}×`,
    detail: criterion.first > criterion.second ? spec.options[0].label : spec.options[1].label,
  };
}

function format(value: number, decimals: number): string {
  return decimals === 0 ? String(Math.round(value)) : value.toFixed(decimals);
}
